import { ActionTree, GetterTree, Module, MutationTree } from 'vuex';
import { httpClient } from '../core';
import { LanguageModel } from '../models/language.model';
import { UploadedFileModel, TranslatedFileModel } from '../models/file.model';
import { CustomTranslation } from '../models/customtranslation.model';
import { env } from '@/env';
import { Guid } from '../services/guid';

export interface TranslateState {
  // Languages
  translateFromLanguages: LanguageModel[];
  translateFromLanguagesFiltered: LanguageModel[];
  selectedFromLanguage: string;

  translateToLanguages: LanguageModel[];
  translateToLanguagesFiltered: LanguageModel[];
  selectedToLanguage: string;

  isFetchingLanguages: boolean;

  // Files
  originalFiles: File[];
  uploadedFiles: UploadedFileModel[];
  translatedFiles: TranslatedFileModel[];

  numberOfFilesToUpload: number;
  numberOfFilesUploaded: number;
  numberOfFilesTranslated: number;

  isUploadingFiles: boolean;
  isTranslatingFiles: boolean;

  customTranslation: CustomTranslation;
}

export const state: TranslateState = {
         // Languages
         translateFromLanguages: new Array<LanguageModel>(),
         translateFromLanguagesFiltered: new Array<LanguageModel>(),
         selectedFromLanguage: 'Auto-Detect',

         translateToLanguages: new Array<LanguageModel>(),
         translateToLanguagesFiltered: new Array<LanguageModel>(),
         selectedToLanguage: '',

         isFetchingLanguages: false,

         // Files
         originalFiles: [],
         uploadedFiles: [],
         translatedFiles: [],

         numberOfFilesToUpload: 0,
         numberOfFilesUploaded: 0,
         numberOfFilesTranslated: 0,

         isUploadingFiles: false,
         isTranslatingFiles: false,

         customTranslation: {
           TranslateTo: '',
           TranslateFrom: '',
           FileName: '',
           ApplyOcr: '',
           Model: '',
           RequestId: new Guid(),
           CustomTranslationOptions: {
             WorkspaceId: '',
             ProjectId: '',
             CategoryId: ''
           }
         }
       };

export const getters: GetterTree<TranslateState, any> = {
  getTranslateFromLanguages: () => {
    return state.translateFromLanguages;
  },
  getTranslateToLanguages: () => {
    return state.translateToLanguages;
  },
  getTranslateFromFilteredLanguages: () => (model: string) => {
    state.translateFromLanguagesFiltered = state.translateFromLanguages;
    if(state.translateFromLanguagesFiltered.length > 0) {
      return state.translateFromLanguagesFiltered.filter(item => item.model.indexOf(model.toLowerCase()) >= 0);
    }
  },
  getTranslateToFilteredLanguages: () => (model: string) => {
    state.translateToLanguagesFiltered = state.translateToLanguages;
    if(state.translateToLanguagesFiltered.length > 0) {
      return state.translateToLanguagesFiltered.filter(item => item.model.indexOf(model.toLowerCase()) >= 0);
    }
  },
  getUploadedFiles: () => {
    return state.uploadedFiles;
  },
  getTranslatedFiles: () => {
    return state.translatedFiles;
  },
  isTranslationCompleted: () => {
    return state.numberOfFilesUploaded === 0 || state.numberOfFilesUploaded === state.numberOfFilesTranslated;
  },
  isFetchingLanguages: () => {
    return state.isFetchingLanguages;
  },
  isUploadingFiles: () => {
    return state.isUploadingFiles;
  },
  isTranslatingFiles: () => {
    return state.isTranslatingFiles;
  },
  getResolvedLanguageName: () => (abbr: string) => {
    const language = state.translateToLanguages.filter((element) => {
      return element.abbr === abbr;
    })[0];
    let languageName = '';
    if (language) {
      languageName = language.language;
    }
    if(languageName === null || languageName === undefined || languageName === '')
      languageName = '[Auto]';

    return languageName;
  }
};

export const mutations: MutationTree<TranslateState> = {
  SET_TRANSLATION_LANGUAGES(stateParam, languages) {
    stateParam.translateFromLanguages = [...languages];
    if (env.topLanguages) {
      stateParam.translateFromLanguages.splice(
        env.topLanguages.split(',').length + 1,
        0,
        { language: 'Auto-Detect', abbr: ' ', model: ['cognitive', 'delta'] }
      );
    }
    stateParam.translateToLanguages = [...languages];
  },
  SET_FILES_TO_TRANSLATE(stateParam, files) {
    stateParam.originalFiles = files;
    stateParam.numberOfFilesToUpload = files.length;
    stateParam.numberOfFilesUploaded = 0;
    stateParam.numberOfFilesTranslated = 0;
  },
  ADD_UPLOADED_FILE(stateParam, uploadedFile) {
    if (!stateParam.uploadedFiles.some((file) => file.fileName === uploadedFile.fileName)) {
      stateParam.uploadedFiles.push(uploadedFile);
    }
    stateParam.numberOfFilesUploaded++;
    if (stateParam.numberOfFilesToUpload === stateParam.numberOfFilesUploaded) {
      stateParam.isUploadingFiles = false;
    }
  },
  ADD_TRANSLATED_FILE(stateParam, translatedFile) {
    const existingFile = stateParam.translatedFiles.find((file) => file.fileName === translatedFile.fileName);

    if (!existingFile) {
      stateParam.translatedFiles.push(translatedFile);
    } else {
      existingFile.translatedCorrectly = translatedFile.translatedCorrectly;
      existingFile.documentId = translatedFile.documentId;
      existingFile.totalBytes = translatedFile.totalBytes;
      existingFile.totalCharacters = translatedFile.totalCharacters;
    }
    stateParam.numberOfFilesTranslated++;
    if (stateParam.numberOfFilesUploaded === stateParam.numberOfFilesTranslated) {
      stateParam.isTranslatingFiles = false;
    }
  },
  SET_FETCHING_LANGUAGES() {
    state.isFetchingLanguages = true;
  },
  UNSET_FETCHING_LANGUAGES() {
    state.isFetchingLanguages = false;
  },
  RESET_STATE() {
    state.originalFiles = [];
    state.uploadedFiles = [];
    state.translatedFiles = [];
    state.translatedFiles.splice(0, state.translatedFiles.length);
    state.numberOfFilesToUpload = 0;
    state.numberOfFilesUploaded = 0;
    state.numberOfFilesTranslated = 0;
  },
  SET_UPLOADING_FILES(stateParam) {
    stateParam.isUploadingFiles = true;
  },
  UNSET_UPLOADING_FILES(stateParam) {
    stateParam.isUploadingFiles = false;
  },
  SET_TRANSLATING_FILES(stateParam) {
    stateParam.isTranslatingFiles = true;
  },
  ADDED_FB() {
    console.log('ADDED_FB');
  }
};

export const actions: ActionTree<TranslateState, any> = {

  healthCheck: () => {
    return new Promise((resolve, reject) => {
      const healthApi = env.apiUrl?.replace("/api", "");
      httpClient.instance
        .get(`${healthApi}/areyouhealthy`)
        .then((data) => {
          resolve(data);
        })
        .catch((error) => {
          reject(error);
        })
        ;
    });
  },

  fetchLanguages: ({ commit }) => {
    return new Promise((resolve, reject) => {
      commit('SET_FETCHING_LANGUAGES');
      httpClient.instance
        .get(`${env.apiUrl}/languages/${env.applicationId}`)
        .then((languagesResponse) => {
          // The languages come in an object and we have to transform it to an Array of Objects
          const languagesObject = languagesResponse.data.sort(
            (a: any, b: any) => {
              const nameA = a.language!.toLowerCase(); // ignore upper and lowercase
              const nameB = b.language!.toLowerCase(); // ignore upper and lowercase
              if (nameA < nameB) {
                return -1;
              }
              if (nameA > nameB) {
                return 1;
              }
              // names must be equal
              return 0;
            }
          );
          const languagesArray: Array<LanguageModel | { divider: any, abbr: string, language: string }> = [];
          if (env.topLanguages) {
                                  const topLanguages = env.topLanguages
                                    .split(',')
                                    .map((l: any) => {
                                      return {
                                        language: languagesObject.find(
                                          (m: any) => {
                                            return m.code === l;
                                          }
                                        ).language,
                                        model: languagesObject.find(
                                          (m: any) => {
                                            return m.code === l;
                                          }
                                        ).model,
                                        abbr: l
                                      };
                                    });
                                  for (const lang of topLanguages) {
                                    languagesArray.push(lang);
                                  }

                                  languagesArray.push({
                                    divider: '_',
                                    abbr: '',
                                    language: '',
                                    model: ['cognitive', 'delta']
                                  });

                                  for (const key of languagesObject) {
                                    let abbr = key.code;
                                    if (
                                      topLanguages.find(
                                        (l: any) => l.abbr == key.code
                                      )
                                    ) {
                                      abbr = `${key.code}|clone`;
                                    }
                                    const l = languagesObject.find(
                                      (m: any) => {
                                        return m.code === abbr || m.code === abbr.split('|clone')[0] || abbr === '';
                                      });

                                    languagesArray.push({
                                      language: l.language,
                                      abbr: abbr,
                                      model: l.model
                                    });
                                  }
                                }

          commit('SET_TRANSLATION_LANGUAGES', languagesArray);
          commit('UNSET_FETCHING_LANGUAGES');
          resolve(languagesResponse);
        })
        .catch((error) => {
          commit('UNSET_FETCHING_LANGUAGES');
          reject(error);
        })
        .finally(() => {
          commit('UNSET_FETCHING_LANGUAGES');
        });
    });
  },
  uploadFilesToTranslate: async ({ commit }, payload) => {
    commit('SET_UPLOADING_FILES');
    let formData = null;
    const g = new Guid();
    const gg = g.newGuid();
    for (const file of payload.files) {
      formData = new FormData();

      state.customTranslation = {
        TranslateTo: payload.toLanguage.trim(),
        TranslateFrom: payload.fromLanguage.trim(),
        FileName: file.name,
        ApplyOcr: file.ApplyOcr,
        Model: payload.model,
        RequestId: gg,
        CustomTranslationOptions: {
          WorkspaceId: file.selectedDictionary.workspaceId,
          ProjectId: file.selectedDictionary.projectId,
          CategoryId: file.selectedDictionary.categoryId
        }
      };

      formData.append('File', file);
      formData.append('Options', JSON.stringify(state.customTranslation));
      try {
        const response = await httpClient.instance.post(`${env.apiUrl}/documents/${env.applicationId}/translate`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          });

        // console.log('File upload SUCCESS!!');
        const uploadedFile: UploadedFileModel = {
          documentId: response.data.documentId,
          fileName: response.data.fileName,
          originalFileName: file.name,
          uploadedCorrectly: true,
          model: response.data.model
        };
        commit('ADD_UPLOADED_FILE', uploadedFile);
      } catch (error) {
        // eslint-disable-next-line
        console.error('File Upload FAILURE!! ' + error);
        const uploadedFile: UploadedFileModel = {
          documentId: '-1',
          fileName: '',
          originalFileName: state.customTranslation.FileName,
          model: '',
          uploadedCorrectly: false
        };
        commit('ADD_UPLOADED_FILE', uploadedFile);
      }
    }
    commit('SET_FILES_TO_TRANSLATE', payload.files);
    commit('UNSET_UPLOADING_FILES');
  },
  checkFileStatus: ({ commit, dispatch }, payload) => {
    commit('SET_TRANSLATING_FILES');
    let inProgress = false;
    let isCompleted = false;
    let intervalInProgress = false;
    const intervalId = setInterval(async () => {
      if (intervalInProgress) {
        return;
      }
      intervalInProgress = true;
      try {
        const url = `${env.apiUrl}/documents/${env.applicationId}/${payload.uploadResponse.data.documentId}/status`;
        const statusResponse = await httpClient.instance.get(url);

        inProgress = statusResponse.data.status === 'INPROGRESS';
        isCompleted = statusResponse.data.status === 'COMPLETED';
        // console.log(`File ${payload.uploadResponse.data.documentId},
        //             Status: ${statusResponse.data.status},
        //             Details: ${statusResponse.data.statusDetails}`);
      } catch (error) {
        // console.log('Error getting file status');
      } finally {
        if (!inProgress) {
          clearInterval(intervalId);
          dispatch('addTranslatedFile', {
            data: payload.uploadResponse.data,
            isTranslatedCorrectly: isCompleted
          });
        }
        intervalInProgress = false;
      }
    }, Math.floor(Math.random() * 3000 + 5000));
  },
  addTranslatedFile: ({ commit }, fileData) => {
    const translatedFile: TranslatedFileModel = {
      documentId: fileData.data.documentId,
      fileName: fileData.data.fileName,
      originalName: fileData.data.originalName,
      translatedCorrectly: fileData.isTranslatedCorrectly,
      dateTime: fileData.data.datetime,
      sourceLang: fileData.data.sourceLang,
      destinationLang: fileData.data.destinationLang,
      totalBytes: fileData.data.totalBytes,
      totalCharacters: fileData.data.totalCharacters,
      statusId: fileData.data.statusId,
      model: fileData.data.model
    };
    commit('ADD_TRANSLATED_FILE', translatedFile);
  },
  resetState: ({ commit }) => {
    commit('RESET_STATE');
  },

  //Feedback Service
  addFeedback: ({ commit }, payload) => {
    return new Promise((resolve, reject) => {
      //commit('SET_ADDING_FB');
      let formData = null;

      formData = new FormData();

      if(payload.firstFile) {
        formData.append('File', payload.firstFile);
      }
      if(payload.secondFile) {
        formData.append('File', payload.secondFile);
      }
      if(payload.applicationId) {
        formData.append('ApplicationId', payload.applicationId); //NOTE: optional, can be used as subapp
      }
      formData.append('Url', payload.url);
      formData.append('Text', payload.text);

      if(payload.isUseful !== null && payload.isUseful !== undefined)
        formData.append('IsUseful', payload.isUseful);

      if(payload.rating !== null && payload.rating !== undefined)
        formData.append('Rating', payload.rating);

      if(payload.isAnonymous !== null && payload.isAnonymous !== undefined)
        formData.append('IsAnonymous', payload.isAnonymous);

      httpClient.instance
        .post(`${env.apiUrl}/feedback/${env.applicationId}/presubmit`,
          formData,
          {
            headers: { 'Content-Type': 'multipart/form-data' },
            onUploadProgress: payload.onUploadProgress
          }
        )
        .then(response => {
          commit('ADDED_FB', response);
          resolve(response);
        })
        .catch(error => {
          if(!error.response) {
            reject(error)
          }
          else
            reject(error.response.data);
        })
        .finally(() => {
          //commit('UNSET_ADDING_FB');
        });
    })
  }
};

export const translate: Module<TranslateState, any> = {
  state,
  getters,
  mutations,
  actions,
  namespaced: true
};
